// ============================================================================
// Sub Replacement — AM-initiated create-request form.
//
// Phase 1 fields (per the 2026-05-12 prompt):
//   - Facility            (combobox; queries active_facilities, RLS-scoped)
//   - Outgoing assignment (combobox; depends on facility selection)
//   - Reason              (free-form textarea, internal-only — uses accurate language)
//   - Urgency             (Emergency / Soon / Planned — matches Supply Request tiers)
//   - Hold pay            (switch, default OFF — README Open Item #4)
//   - Notes               (optional)
//
// Voice-first capture is Phase 2 — not in this Phase 1 build. The form is the
// primary entry surface; the field set is small enough to type quickly.
// ============================================================================

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { toNumOrNull } from '../../shared/coerce';
import {
  Button,
  ErrorState,
  PageHeader,
  UrgencyBadge,
} from '../../shared/components';
import { SmartSelect } from '../../components/smart-select';
import type {
  NewSubReplacementRequestInput,
  SubReplacementUrgency,
} from '../../shared/types';
import { SUB_REPLACEMENT_URGENCY_TIERS } from '../../shared/types';
import {
  createSubReplacementRequest,
  useAssignmentOptions,
  useFacilityOptions,
  type FacilityOption,
} from './hooks/use-sub-replacement-data';
import { errorMessage } from '../../shared/errors';
import { SUB_CHANGE_REASON_HELP, useSubChangeReasons } from './sub-change-reasons';
import { payHint, payPlaceholder, payUnit } from './pay-unit';

interface FormState {
  facility: FacilityOption | null;
  outgoingAssignmentId: string | null;
  /**
   * Optional incoming sub. The AM often doesn't know this at request time —
   * if known, captured in sub_replacement_requests.incoming_provider_id.
   * Scoped via SmartSelect to providers active at the chosen facility.
   */
  incomingProviderId: string | null;
  /** Why the sub is changing, from the list (#667). Required. */
  reasonCode: string | null;
  reason: string;
  urgency: SubReplacementUrgency;
  holdPay: boolean;
  // AFB-0027 — pay / incentives / change date, all captured at request time
  // (Jack's "one step" principle). String-typed inputs; converted on submit.
  // '' → NULL (never 0): not given yet. Needed at the changeover, never
  // carried over (21 Aug rule). Per hour on an hourly line (item 896).
  incomingPayRate: string;
  nmiEnabled: boolean;
  nmiAmount: string;
  complimentEnabled: boolean;
  complimentAmount: string;
  plannedChangeDate: string; // yyyy-mm-dd
  changeDateType: 'planned' | 'historical';
  notes: string;
}

const INITIAL: FormState = {
  facility: null,
  outgoingAssignmentId: null,
  incomingProviderId: null,
  reasonCode: null,
  reason: '',
  urgency: 'Soon',
  holdPay: false,
  incomingPayRate: '',
  nmiEnabled: false,
  nmiAmount: '',
  complimentEnabled: false,
  complimentAmount: '',
  plannedChangeDate: '',
  changeDateType: 'planned',
  notes: '',
};

/** '' → NULL (not given yet); anything else → number. Never coerce '' to 0. */
const numOrNull = (s: string): number | null => toNumOrNull(s);

/** Local-timezone yyyy-mm-dd for the date input's min/max bounds. */
function localISODate(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function SubReplacementForm() {
  const navigate = useNavigate();
  const { employee } = useAuth();

  const { facilities, isLoading: facsLoading } = useFacilityOptions();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { offered: reasonOptions, loading: reasonsLoading, error: reasonsError } = useSubChangeReasons();

  // 2026-06-15 — prefill from the deep-link the facility detail page builds.
  // "Assign sub" (no active sub) links here with ?facility_id=…; "Change sub"
  // adds &assignment_id=…. Consuming these makes both flows land ready instead
  // of forcing the AM to re-pick the facility they just came from. Runs once
  // after facilities resolve so RLS-scoped options are available to match.
  const [searchParams] = useSearchParams();
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    const fid = searchParams.get('facility_id');
    if (!fid || facsLoading) return;
    const match = facilities.find((f) => f.facility_id === fid);
    if (!match) return;
    prefilledRef.current = true;
    const asgId = searchParams.get('assignment_id');
    setForm((prev) => ({
      ...prev,
      facility: match,
      outgoingAssignmentId: asgId || prev.outgoingAssignmentId,
    }));
  }, [facilities, facsLoading, searchParams]);

  const { assignments, isLoading: asgLoading } = useAssignmentOptions(
    form.facility?.facility_id ?? null,
  );
  const today = localISODate();

  // When facility changes, clear the outgoing assignment so the AM picks again.
  // (and auto-select if there's exactly one Active assignment, which is the
  // common case — most facilities have a single primary assignment).
  useEffect(() => {
    if (assignments.length === 1) {
      setForm((prev) => ({
        ...prev,
        outgoingAssignmentId: assignments[0].assignment_id,
      }));
    } else if (
      form.outgoingAssignmentId &&
      !assignments.some((a) => a.assignment_id === form.outgoingAssignmentId)
    ) {
      setForm((prev) => ({ ...prev, outgoingAssignmentId: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments]);

  // AFB-0027 — carry-forward defaults. When the outgoing assignment resolves,
  // prefill the incentive config from its current values (editable after).
  // Compliment amount has no per-assignment source column; stays blank.
  const selectedAssignment = useMemo(
    () =>
      assignments.find((a) => a.assignment_id === form.outgoingAssignmentId) ??
      null,
    [assignments, form.outgoingAssignmentId],
  );
  useEffect(() => {
    if (!selectedAssignment) return;
    setForm((prev) => ({
      ...prev,
      nmiEnabled: Boolean(selectedAssignment.no_misses_bonus_enabled),
      nmiAmount:
        selectedAssignment.no_misses_bonus_amount != null
          ? String(selectedAssignment.no_misses_bonus_amount)
          : '',
      complimentEnabled: Boolean(
        selectedAssignment.compliment_incentive_enabled,
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssignment?.assignment_id]);

  const canSubmit = useMemo(() => {
    return Boolean(
      form.facility &&
        form.facility.account_id &&
        form.reasonCode &&
        form.reason.trim().length > 0 &&
        !submitting,
    );
  }, [form, submitting]);

  if (!employee) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <ErrorState
          title="No employee record"
          body="Your account isn't linked to an employee row. Ask an admin to update your user profile before opening Sub Replacement requests."
        />
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.facility || !form.facility.account_id || !employee) return;
    // ⛔ A sub change replaces an arrangement. Without one there is nothing to
    // change, and the path that used to accept this invented a line priced at
    // the whole building. Refused here so nobody fills the form in first.
    if (!form.outgoingAssignmentId) {
      setError(
        'Choose the arrangement that is ending. If there is no sub on this building yet, a first sub is recorded on the service line itself — open the line on the building’s Crew & Sub card and name the sub there.',
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: NewSubReplacementRequestInput = {
        facility_id: form.facility.facility_id,
        account_id: form.facility.account_id,
        outgoing_assignment_id: form.outgoingAssignmentId,
        incoming_provider_id: form.incomingProviderId,
        requested_by_eid: employee.employee_id,
        reason: form.reason,
        reason_code: form.reasonCode as string,
        urgency: form.urgency,
        hold_pay: form.holdPay,
        // Blank = NULL = not given yet (needed at the changeover). numOrNull
        // never returns 0 for ''. Its unit is the outgoing line's (item 896).
        incoming_pay_rate: numOrNull(form.incomingPayRate),
        nmi_enabled: form.nmiEnabled,
        nmi_amount: form.nmiEnabled ? numOrNull(form.nmiAmount) : null,
        compliment_enabled: form.complimentEnabled,
        compliment_amount: form.complimentEnabled
          ? numOrNull(form.complimentAmount)
          : null,
        planned_change_date: form.plannedChangeDate || null,
        // Nobody picks this any more - it is read off the date. Retiring the
        // column belongs to the step that drops it, not here.
        change_date_type: form.plannedChangeDate
          ? (form.plannedChangeDate < today ? 'historical' : 'planned')
          : null,
        notes: form.notes.trim() || null,
      };
      const result = await createSubReplacementRequest(payload);
      if (!result) throw new Error('Insert returned no row.');
      // Phase 1 toast: per prompt copy. Navigates to detail so the AM can
      // confirm the row landed and copy the SRR-NNNN ID.
      navigate(`/sub-replacement/${result.request_id}`, {
        state: {
          flash: `Replacement request ${result.request_id} submitted. Awaiting COO approval.`,
        },
      });
    } catch (err) {
      console.error('[sub-replacement] submit failed', err);
      setError(
        errorMessage(err, 'Could not submit the request. Try again.'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl p-4 pb-28 sm:p-6">
      <PageHeader
        title="New replacement request"
        meta="Internal — full subcontractor language allowed"
        actions={
          <Button
            variant="ghost"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/sub-replacement')}
          >
            Back
          </Button>
        }
      />

      {error && (
        <div
          className="mt-4 rounded-md border border-rag-red/30 bg-rag-red/5 px-3 py-2 text-sm text-rag-red"
          role="alert"
        >
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-6">
        {/* Facility ------------------------------------------------------- */}
        {/*
          2026-05-20: Flat <select> swapped for SmartSelect (existing → add
          new → show all). RLS still narrows to is_my_facility for AMs; the
          component just renders the same scoped list more usefully.
          The incoming-sub surface — flagged as missing in the original Cowork
          prompt — was added later the same day (Cowork E followup); see the
          "Incoming sub" fieldset below.
        */}
        <fieldset className="space-y-2">
          <label
            htmlFor="srr-facility"
            className="block text-sm font-medium text-tcs-text"
          >
            Facility
          </label>
          <SmartSelect
            entity="facility"
            value={form.facility?.facility_id ?? null}
            onChange={(id, row) => {
              // Prefer the form hook's row (canonical FacilityOption shape),
              // fall back to SmartSelect's raw row when the hook hasn't
              // resolved yet (rare — same active_facilities source).
              const fac =
                facilities.find((f) => f.facility_id === id) ??
                (row
                  ? ({
                      facility_id: id,
                      facility_name:
                        (row.raw.facility_name as string | null) ?? null,
                      account_id: (row.raw.account_id as string | null) ?? null,
                      market_region:
                        (row.raw.market_region as string | null) ?? null,
                    } as FacilityOption)
                  : null);
              setForm((prev) => ({
                ...prev,
                facility: fac ?? null,
                outgoingAssignmentId: null,
              }));
            }}
            placeholder={
              facsLoading ? 'Loading facilities…' : 'Search facilities…'
            }
            disabled={facsLoading}
          />
          <p className="font-mono text-[11px] text-tcs-text-muted">
            Filtered to your assigned facilities via RLS.
          </p>
        </fieldset>

        {/* Outgoing assignment ------------------------------------------- */}
        <fieldset className="space-y-2">
          <label
            htmlFor="srr-assignment"
            className="block text-sm font-medium text-tcs-text"
          >
            Outgoing assignment <span className="text-rag-red">*</span>
          </label>
          <select
            id="srr-assignment"
            className="w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            value={form.outgoingAssignmentId ?? ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                outgoingAssignmentId: e.target.value || null,
              }))
            }
            disabled={!form.facility || asgLoading}
          >
            <option value="">
              {!form.facility
                ? 'Pick a facility first'
                : asgLoading
                  ? 'Loading…'
                  : assignments.length === 0
                    ? 'No sub on this building yet'
                    : 'Select assignment…'}
            </option>
            {assignments.map((a) => (
              <option key={a.assignment_id} value={a.assignment_id}>
                {a.role_title ?? '—'} · {a.provider_name ?? a.provider_id ?? 'unassigned'} ({a.assignment_id})
              </option>
            ))}
          </select>
          {/* ⛔ A FIRST SUB IS NOT A SUB CHANGE. This used to say "optional —
              leave blank if this is a new role with no incumbent", and the path
              behind a blank one went looking for a line already carrying the
              incoming sub, with the job name hardcoded to one spelling. When it
              found nothing it created a line priced at the WHOLE BUILDING: three
              phantom lines across two buildings in three days. The database now
              refuses a cutover with no outgoing line; the form says so first, so
              nobody fills in a whole request before being told. */}
          <p className="text-[11px] text-tcs-text-secondary">
            {form.facility && !asgLoading && assignments.length === 0
              ? 'There is no sub on this building to change. A first sub is recorded on the service line itself, on the building’s Crew & Sub card — open the line and name the sub there.'
              : 'Which arrangement is ending. A sub change always replaces one — recording a sub for the first time happens on the line, not here.'}
          </p>
        </fieldset>

        {/* Incoming sub --------------------------------------------------- */}
        {/*
          2026-05-20: Cowork E. Captures the AM's intent at request time
          when the replacement sub is already known. Optional. Scoped via
          SmartSelect entity="service_provider" with facility_id scope so
          the list narrows to providers currently active in this facility's
          state/market. Persisted into sub_replacement_requests.incoming_provider_id.
        */}
        <fieldset className="space-y-2">
          <label
            htmlFor="srr-incoming"
            className="block text-sm font-medium text-tcs-text"
          >
            Incoming sub <span className="text-tcs-text-muted">(optional)</span>
          </label>
          <SmartSelect
            entity="service_provider"
            scope={
              form.facility?.facility_id
                ? { facility_id: form.facility.facility_id }
                : undefined
            }
            value={form.incomingProviderId}
            onChange={(id) =>
              setForm((prev) => ({ ...prev, incomingProviderId: id || null }))
            }
            placeholder={
              !form.facility
                ? 'Pick a facility first…'
                : 'Search subs in this market…'
            }
            entityLabel="sub"
            disabled={!form.facility}
          />
          <p className="font-mono text-[11px] text-tcs-text-muted">
            Captures intent only. The actual handoff is tracked separately.
          </p>
        </fieldset>

        {/* New sub pay (AFB-0027) ------------------------------------------ */}
        <fieldset className="space-y-2">
          <label
            htmlFor="srr-pay"
            className="block text-sm font-medium text-tcs-text"
          >
            Incoming contract rate ({payUnit(selectedAssignment)}){' '}
            <span className="text-tcs-text-muted">(optional now, needed at the changeover)</span>
          </label>
          <input
            id="srr-pay"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            className="w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            placeholder={payPlaceholder(selectedAssignment)}
            value={form.incomingPayRate}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, incomingPayRate: e.target.value }))
            }
          />
          <p className="font-mono text-[11px] text-tcs-text-muted">
            {payHint(selectedAssignment)}
          </p>
        </fieldset>

        {/* Incentives (AFB-0027) ------------------------------------------- */}
        <fieldset className="space-y-3 rounded-md border border-tcs-border bg-tcs-bg p-3">
          <legend className="px-1 text-sm font-medium text-tcs-text">
            Performance payments for the incoming sub
          </legend>
          <p className="-mt-1 text-[12px] text-tcs-text-muted">
            Defaults carry forward from the outgoing assignment. Editable any
            time before cutover — blanks are fine at approval.
          </p>
          <label className="flex items-center gap-2 text-sm text-tcs-text">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-tcs-border"
              checked={form.nmiEnabled}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, nmiEnabled: e.target.checked }))
              }
            />
            Scope Performance Payment
          </label>
          {form.nmiEnabled && (
            <label className="block text-xs text-tcs-text-muted">
              Scope Performance Payment ($/mo)
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                className="mt-1 block w-full max-w-[12rem] rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                value={form.nmiAmount}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, nmiAmount: e.target.value }))
                }
              />
            </label>
          )}
          <label className="flex items-center gap-2 text-sm text-tcs-text">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-tcs-border"
              checked={form.complimentEnabled}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  complimentEnabled: e.target.checked,
                }))
              }
            />
            Compliment payment
          </label>
          {form.complimentEnabled && (
            <label className="block text-xs text-tcs-text-muted">
              Compliment amount ($)
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                className="mt-1 block w-full max-w-[12rem] rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                value={form.complimentAmount}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    complimentAmount: e.target.value,
                  }))
                }
              />
            </label>
          )}
        </fieldset>

        {/* The night the new sub starts. ONE field, no mode to pick first.
            Ben's ruling 2026-09-08: yesterday, today and tomorrow are all
            valid through the same path, and a date in the past is the ORDINARY
            case - 46 of the 58 dated sub changes on file were recorded after
            the fact. The old version made the AM classify the change as
            "planned" or "historical" BEFORE it would let them type a date, and
            then refused anything outside the range that choice implied. That
            is paperwork standing in front of reality. ------------------- */}
        <fieldset className="space-y-2">
          <legend className="block text-sm font-medium text-tcs-text">
            First night for the new sub{' '}
            <span className="text-tcs-text-muted">(optional)</span>
          </legend>
          <input
            id="srr-change-date"
            type="date"
            className="w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            value={form.plannedChangeDate}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, plannedChangeDate: e.target.value }))
            }
          />
          <p className="text-[11px] text-tcs-text-muted">
            {form.plannedChangeDate === ''
              ? 'Already happened, happening tonight, or booked for later - all fine here.'
              : form.plannedChangeDate < today
                ? 'Already happened. Recorded as of that night so the month splits correctly.'
                : form.plannedChangeDate > today
                  ? 'Booked. Nothing changes at the building until that night.'
                  : 'Starting tonight.'}
          </p>
        </fieldset>

        {/* Why — from the list (#667). The loss screens read this, not the words. */}
        <fieldset className="space-y-2" aria-describedby="srr-why-help">
          <legend className="block text-sm font-medium text-tcs-text">
            Why is this sub changing?
          </legend>
          {reasonsError ? (
            <p role="alert" className="rounded-md border border-tcs-border bg-tcs-surface px-3 py-2 text-sm text-st-danger-fg">
              The list of reasons did not load, so this change cannot be saved yet. Reload the page; if it keeps happening, tell Ben. ({reasonsError})
            </p>
          ) : reasonsLoading ? (
            <p className="text-sm text-tcs-text-muted">Loading reasons…</p>
          ) : (
            <div className="space-y-1.5" role="radiogroup" aria-label="Why is this sub changing?">
              {reasonOptions.map((r) => {
                const on = form.reasonCode === r.reason_code;
                return (
                  <label
                    key={r.reason_code}
                    className={`flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2 ${
                      on ? 'border-tcs-inverse bg-tcs-inverse/5' : 'border-tcs-border hover:border-tcs-inverse/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="srr-why"
                      className="mt-1"
                      value={r.reason_code}
                      checked={on}
                      onChange={() => setForm((prev) => ({ ...prev, reasonCode: r.reason_code }))}
                    />
                    <span>
                      <span className="block text-sm font-medium text-tcs-text">{r.label}</span>
                      {SUB_CHANGE_REASON_HELP[r.reason_code] && (
                        <span className="block text-xs text-tcs-text-secondary">
                          {SUB_CHANGE_REASON_HELP[r.reason_code]}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <p id="srr-why-help" className="text-[11px] text-tcs-text-muted">
            Required. The At risk tab counts an ended assignment only for the reasons that count against the sub.
          </p>
        </fieldset>

        <fieldset className="space-y-2">
          <label
            htmlFor="srr-reason"
            className="block text-sm font-medium text-tcs-text"
          >
            What happened, in words
          </label>
          <textarea
            id="srr-reason"
            className="min-h-[100px] w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            placeholder="What happened — the approver reads this. e.g. Floors in the ORs not done three nights running; client asked twice."
            value={form.reason}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, reason: e.target.value }))
            }
            required
          />
        </fieldset>

        {/* Urgency -------------------------------------------------------- */}
        <fieldset className="space-y-2">
          <legend className="block text-sm font-medium text-tcs-text">
            Urgency
          </legend>
          <div className="grid grid-cols-3 gap-2">
            {SUB_REPLACEMENT_URGENCY_TIERS.map((tier) => {
              const active = form.urgency === tier;
              return (
                <button
                  key={tier}
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, urgency: tier }))
                  }
                  className={`flex items-center justify-center rounded-md border px-3 py-2 transition ${
                    active
                      ? 'border-tcs-inverse bg-tcs-inverse/5'
                      : 'border-tcs-border hover:border-tcs-inverse/40'
                  }`}
                  aria-pressed={active}
                >
                  <UrgencyBadge tier={tier} withTimeline />
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Hold pay ------------------------------------------------------- */}
        <fieldset className="flex items-start justify-between gap-3 rounded-md border border-tcs-border bg-tcs-bg p-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-tcs-text">
              Final payment per the Subcontractor Agreement
            </div>
            <p className="mt-0.5 text-[12px] text-tcs-text-muted">
              Default off. Holds the outgoing subcontractor&rsquo;s final payment until
              the handover is complete, per the agreement&rsquo;s termination terms.
            </p>
          </div>
          <label className="inline-flex shrink-0 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-tcs-border"
              checked={form.holdPay}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, holdPay: e.target.checked }))
              }
            />
            <span className="font-mono text-[11px] uppercase tracking-wide text-tcs-text-muted">
              {form.holdPay ? 'ON' : 'OFF'}
            </span>
          </label>
        </fieldset>

        {/* Notes ---------------------------------------------------------- */}
        <fieldset className="space-y-2">
          <label
            htmlFor="srr-notes"
            className="block text-sm font-medium text-tcs-text"
          >
            Notes <span className="text-tcs-text-muted">(optional)</span>
          </label>
          <textarea
            id="srr-notes"
            className="min-h-[60px] w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            placeholder="Anything else the approver should know."
            value={form.notes}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, notes: e.target.value }))
            }
          />
        </fieldset>

        {/* SOFT nudge (AFB-0027 item 5) — encourage pay/date, never block. */}
        {(form.incomingPayRate.trim() === '' ||
          form.plannedChangeDate === '') && (
          <div className="rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-[12px] text-st-warn-fg-strong">
            {form.incomingPayRate.trim() === '' && form.plannedChangeDate === ''
              ? 'Heads up: no new contract rate and no change date yet. The contract rate is needed at the changeover — the current rate does not carry over. Add both now if you know them.'
              : form.incomingPayRate.trim() === ''
                ? 'No new contract rate entered. It is needed at the changeover — the current rate does not carry over. Add it now if you know it.'
                : 'No date of change yet — add it now if you know it. It stays editable until cutover.'}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={() => navigate('/sub-replacement')}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : (
              'Submit for approval'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
